import { useState, useRef, useEffect } from 'react'
import { NavLink, Link, useLocation } from 'react-router-dom'

export interface NavMenuItem {
  label: string
  href: string
  external?: boolean
  visible: boolean
  badge?: number
}

export interface NavDropdownMenuProps {
  label: string
  items: NavMenuItem[]
  hubPath?: string
}

export default function NavDropdownMenu({ label, items, hubPath }: NavDropdownMenuProps) {
  const [open, setOpen] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const location = useLocation()

  // Alle hooks MOETEN voor early returns staan (Rules of Hooks)
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.search])

  const visibleItems = items.filter(i => i.visible)

  // Geen zichtbare items → verberg het hele menu
  if (visibleItems.length === 0) return null

  // Check of huidige route matcht met een sub-item
  // Strip query params van href voor correcte vergelijking
  const isGroupActive = visibleItems.some(item => {
    if (item.external) return false
    const path = item.href.split('?')[0]
    return location.pathname.startsWith(path)
  })

  // Heeft het menu een badge?
  const totalBadge = visibleItems.reduce((sum, item) => sum + (item.badge || 0), 0)

  function handleMouseEnter() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setOpen(true)
  }

  function handleMouseLeave() {
    timeoutRef.current = setTimeout(() => setOpen(false), 150)
  }

  return (
    <div
      ref={menuRef}
      className="relative hidden md:block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hoofdmenu label */}
      {hubPath ? (
        <NavLink
          to={hubPath}
          className={`relative cursor-pointer ${
            isGroupActive ? 'text-white font-medium' : 'text-primary-foreground/70 hover:text-white transition-colors'
          }`}
        >
          {label}
          <span className="text-[10px] ml-0.5 opacity-60">▾</span>
          {totalBadge > 0 && (
            <span className="absolute -top-1.5 -right-4 bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          )}
        </NavLink>
      ) : (
        <button
          className={`relative cursor-pointer ${
            isGroupActive ? 'text-white font-medium' : 'text-primary-foreground/70 hover:text-white transition-colors'
          }`}
          onClick={() => setOpen(!open)}
        >
          {label}
          <span className="text-[10px] ml-0.5 opacity-60">▾</span>
          {totalBadge > 0 && (
            <span className="absolute -top-1.5 -right-4 bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {totalBadge > 99 ? '99+' : totalBadge}
            </span>
          )}
        </button>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[220px] z-50">
          {visibleItems.map(item => {
            if (item.external) {
              return (
                <a
                  key={item.href}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {item.label}
                  <span className="text-xs text-gray-400 ml-1">↗</span>
                </a>
              )
            }

            // Vergelijk volledige URL (inclusief query params) voor correcte active-state
            const currentUrl = location.pathname + location.search
            const isActive = item.href.includes('?')
              ? currentUrl === item.href
              : location.pathname === item.href || location.pathname.startsWith(item.href + '/')

            return (
              <Link
                key={item.href}
                to={item.href}
                className={`block px-4 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center justify-between">
                  {item.label}
                  {item.badge && item.badge > 0 ? (
                    <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  ) : null}
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Mobile navigatie ─────────────────────────────────────────────────────────

export interface MobileNavProps {
  groups: NavDropdownMenuProps[]
  plainLinks?: { label: string; href: string; visible: boolean; badge?: number }[]
  onClose: () => void
}

export function MobileNav({ groups, plainLinks = [], onClose }: MobileNavProps) {
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const location = useLocation()

  // Sluit bij route-verandering
  useEffect(() => {
    onClose()
  }, [location.pathname])

  return (
    <div className="fixed inset-0 z-50 bg-primary md:hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-primary-foreground/20">
        <div className="leading-tight">
          <div className="font-bold text-lg text-white">iGO! Professional</div>
          <div className="text-xs text-primary-foreground/80">Learning Hub</div>
        </div>
        <button onClick={onClose} className="text-white text-2xl p-2">✕</button>
      </div>

      <nav className="overflow-y-auto max-h-[calc(100vh-72px)] px-6 py-4 space-y-1">
        {/* Plain links (Home, Mijn profiel) */}
        {plainLinks.filter(l => l.visible).map(link => (
          <NavLink
            key={link.href}
            to={link.href}
            className={({ isActive }) =>
              `block py-3 text-base ${isActive ? 'text-white font-medium' : 'text-primary-foreground/70'}`
            }
          >
            <span className="flex items-center justify-between">
              {link.label}
              {link.badge && link.badge > 0 ? (
                <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                  {link.badge > 99 ? '99+' : link.badge}
                </span>
              ) : null}
            </span>
          </NavLink>
        ))}

        {/* Dropdown groups as accordeons */}
        {groups.map(group => {
          const visibleItems = group.items.filter(i => i.visible)
          if (visibleItems.length === 0) return null

          const isOpen = openGroup === group.label
          const isGroupActive = visibleItems.some(item => {
            if (item.external) return false
            const path = item.href.split('?')[0]
            return location.pathname.startsWith(path)
          })

          return (
            <div key={group.label}>
              <button
                onClick={() => setOpenGroup(isOpen ? null : group.label)}
                className={`w-full flex items-center justify-between py-3 text-base ${
                  isGroupActive ? 'text-white font-medium' : 'text-primary-foreground/70'
                }`}
              >
                {group.label}
                <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>▾</span>
              </button>

              {isOpen && (
                <div className="pl-4 space-y-1 pb-2">
                  {visibleItems.map(item => (
                    item.external ? (
                      <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block py-2 text-sm text-primary-foreground/60"
                      >
                        {item.label} <span className="text-xs">↗</span>
                      </a>
                    ) : (
                      <NavLink
                        key={item.href}
                        to={item.href}
                        className={({ isActive }) =>
                          `block py-2 text-sm ${isActive ? 'text-white font-medium' : 'text-primary-foreground/60'}`
                        }
                      >
                        <span className="flex items-center justify-between">
                          {item.label}
                          {item.badge && item.badge > 0 ? (
                            <span className="bg-orange-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                              {item.badge > 99 ? '99+' : item.badge}
                            </span>
                          ) : null}
                        </span>
                      </NavLink>
                    )
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>
    </div>
  )
}
